import { create } from 'zustand';
import api from '../services/api';

const useRansomwareStore = create((set, get) => ({
  // State
  groups: [],
  filteredGroups: [],
  selectedGroup: null,
  victims: [],
  isLoading: false,
  error: null,
  
  // Pagination state
  currentPage: 1,
  itemsPerPage: 12,
  totalItems: 0,
  totalPages: 0,
  searchTerm: '',

  // Actions
  fetchGroups: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get('/search/ransomware-groups');
      const groups = response.data;
      
      set((state) => {
        const totalItems = groups.length;
        const totalPages = Math.ceil(totalItems / state.itemsPerPage);
        
        return { 
          groups,
          totalItems,
          totalPages,
          isLoading: false,
          error: null
        };
      });
      
      // Apply pagination and filtering
      get().applyFilters();
      
      return response.data;
    } catch (error) {
      set({ 
        isLoading: false,
        error: error.response?.data?.message || 'Failed to fetch ransomware groups'
      });
      throw error;
    }
  },

  fetchVictims: async (groupName) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post('/search/ransomware-victims', { groupName });
      set({ 
        selectedGroup: response.data.rwGroup,
        victims: response.data.victimList,
        isLoading: false 
      });
      return response.data;
    } catch (error) {
      set({ 
        isLoading: false,
        error: error.response?.data?.message || 'Failed to fetch victims'
      });
      throw error;
    }
  },

  // Pagination and filtering
  applyFilters: () => {
    set((state) => {
      // Filter groups if search term is provided
      const filtered = state.searchTerm 
        ? state.groups.filter(group => 
            group.name.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
            (group.description && group.description.toLowerCase().includes(state.searchTerm.toLowerCase()))
          )
        : [...state.groups];
      
      // Calculate total items and pages after filtering
      const totalItems = filtered.length;
      const totalPages = Math.ceil(totalItems / state.itemsPerPage);
      
      // Get current page items
      const startIndex = (state.currentPage - 1) * state.itemsPerPage;
      const endIndex = Math.min(startIndex + state.itemsPerPage, totalItems);
      const paginatedGroups = filtered.slice(startIndex, endIndex);
      
      return {
        filteredGroups: paginatedGroups,
        totalItems,
        totalPages,
        // Reset to page 1 if current page is now out of bounds
        currentPage: state.currentPage > totalPages ? 1 : state.currentPage
      };
    });
  },

  setPage: (page) => {
    set((state) => ({ currentPage: Math.min(Math.max(1, page), state.totalPages) }));
    get().applyFilters();
  },

  setItemsPerPage: (count) => {
    set({ itemsPerPage: count, currentPage: 1 });
    get().applyFilters();
  },

  setSearchTerm: (term) => {
    set({ searchTerm: term, currentPage: 1 });
    get().applyFilters();
  },

  clearSearch: () => {
    set({ searchTerm: '', currentPage: 1 });
    get().applyFilters();
  },

  clearSelection: () => {
    set({ 
      selectedGroup: null,
      victims: [] 
    });
  },

  reset: () => {
    set({ 
      groups: [],
      filteredGroups: [],
      selectedGroup: null,
      victims: [],
      isLoading: false,
      error: null,
      currentPage: 1,
      totalItems: 0,
      totalPages: 0,
      searchTerm: ''
    });
  }
}));

export default useRansomwareStore; 